/* eslint camelcase: ["error", {allow: ["is_locked", "default_max_distance", "default_max_weight", "default_max_volume", "default_time_window", "default_provides"]}] */
import { validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useState } from 'react';
import { ContextualMenu } from '../../../components/menus/ContextualMenu';
import { ConfirmDeleteDialog } from '../../../dialogs/ConfirmDeleteDialog';
import { ConstraintsDialog, ConstraintsType } from '../../../dialogs/ConstraintsDialog';
import { Project } from '../../../domain/project/Project';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useAsUser } from '../../../hooks/session/useAsUser';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useDeleteVehicles } from '../../../hooks/vehicle/useDeleteVehicles';
import { useDisableVehicles } from '../../../hooks/vehicle/useDisableVehicles';
import { useEnableVehicles } from '../../../hooks/vehicle/useEnableVehicles';
import Text from '../../../react-components/Text';
import VehiclesCreatePlanDialog from './VehiclesCreatePlanDialog';

type VehiclesContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type VehiclesActionsMenuType = `contextual` | `popover`;

type VehiclesActionsMenuProps = {
  type?: VehiclesActionsMenuType;
  activeProject?: Project;
  vehicles: Vehicle[];
  contextAnchor?: VehiclesContextAnchor;
  setContextAnchor?: (context?: VehiclesContextAnchor) => void;
  afterDeleteVehicles?: (...args: any[]) => any;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  showCreatePlan?: boolean;
  showChangeConstraintsVehicles?: boolean;
  showDeleteVehicles?: boolean;
  showEnableVehicle?: boolean;
  showDisableVehicle?: boolean;
};

export const VehiclesActionsMenu = ({
  type = `contextual` as VehiclesActionsMenuType,
  vehicles,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  setContextAnchor = () => {},
  afterDeleteVehicles,
  popoverProps,
  showCreatePlan = true,
  showChangeConstraintsVehicles = true,
  showDeleteVehicles = true,
  showEnableVehicle = true,
  showDisableVehicle = true,
}: VehiclesActionsMenuProps) => {
  const { data: user } = useRetrieveUser();
  const { asUser, token } = useAsUser();

  const { data: activeProject } = useRetrieveActiveProject();
  const { run: disableVehicles } = useDisableVehicles();
  const { run: enableVehicles } = useEnableVehicles();
  const { run: deleteVehicles } = useDeleteVehicles();

  const [dialog, setDialog] = useState<`create_plan` | `constraints` | `delete` | undefined>(undefined);

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="vehicles-actions-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showCreatePlan
                ? []
                : [
                    {
                      key: `create_plan`,
                      width: 240,
                      disabled: vehicles.length === 0,
                      onClick: () => setDialog(`create_plan`),
                      content: <Text intlId="vehicles.actions.create_plan" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showChangeConstraintsVehicles
                ? []
                : [
                    {
                      key: `change_constraints`,
                      width: 240,
                      disabled: vehicles.length === 0,
                      onClick: () => setDialog(`constraints`),
                      content: <Text intlId="vehicles.actions.change_constraints" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(vehicles.length === 1 && ((!!asUser && asUser !== ``) || user?.isAdmin() || user?.isRoot())
                ? [
                    {
                      key: `as_vehicle`,
                      width: 240,
                      disabled: !validatePhoneNumber(vehicles[0].phone ?? ``),
                      onClick: () => {
                        window.open(vehicles[0].getAsVehicleLink(token ?? ``), `_blank`);
                      },
                      content: <Text intlId="vehicles.actions.as_vehicle" style={{ cursor: `pointer` }} />,
                    },
                  ]
                : []),
              ...(!showEnableVehicle
                ? []
                : [
                    {
                      key: `enable_vehicle`,
                      width: 240,
                      disabled: vehicles.length === 0 || (vehicles.length === 1 && Boolean(vehicles[0].enabled)),
                      onClick: () => {
                        enableVehicles({ vehicles });
                      },
                      content: <Text intlId="vehicles.actions.enable_vehicle" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showDisableVehicle
                ? []
                : [
                    {
                      key: `disable_vehicle`,
                      width: 240,
                      disabled: vehicles.length === 0 || (vehicles.length === 1 && !vehicles[0].enabled),
                      onClick: () => {
                        disableVehicles({ vehicles });
                      },
                      content: <Text intlId="vehicles.actions.disable_vehicle" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showDeleteVehicles
                ? []
                : [
                    null,
                    {
                      key: `delete`,
                      width: 240,
                      disabled: vehicles.length === 0,
                      onClick: () => setDialog(`delete`),
                      content: (
                        <Text
                          intlId="vehicles.actions.delete"
                          style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showCreatePlan
                  ? [
                      {
                        key: `create_plan`,
                        disabled: vehicles.length === 0,
                        text: <Text intlId="vehicles.actions.create_plan" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`create_plan`);
                        },
                      },
                    ]
                  : []),
                ...(showChangeConstraintsVehicles
                  ? [
                      {
                        key: `change_constraints`,
                        disabled: vehicles.length === 0,
                        text: <Text intlId="vehicles.actions.change_constraints" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`constraints`);
                        },
                      },
                    ]
                  : []),
                ...(vehicles.length === 1 && ((!!asUser && asUser !== ``) || user?.isAdmin() || user?.isRoot())
                  ? [
                      {
                        key: `as_vehicle`,
                        disabled: !validatePhoneNumber(vehicles[0].phone ?? ``),
                        text: <Text intlId="vehicles.actions.as_vehicle" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          window.open(vehicles[0].getAsVehicleLink(token ?? ``), `_blank`);
                        },
                      },
                    ]
                  : []),
                ...(showEnableVehicle
                  ? [
                      {
                        key: `enable_vehicle`,
                        disabled: vehicles.length === 0 || (vehicles.length === 1 && Boolean(vehicles[0].enabled)),
                        text: <Text intlId="vehicles.actions.enable_vehicle" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          enableVehicles({ vehicles });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                        },
                      },
                    ]
                  : []),
                ...(showDisableVehicle
                  ? [
                      {
                        key: `disable_vehicle`,
                        disabled: vehicles.length === 0 || (vehicles.length === 1 && !vehicles[0].enabled),
                        text: <Text intlId="vehicles.actions.disable_vehicle" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          disableVehicles({ vehicles });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                        },
                      },
                    ]
                  : []),
                ...(showDeleteVehicles
                  ? [
                      null,
                      {
                        key: `delete`,
                        disabled: vehicles.length === 0,
                        text: (
                          <Text
                            intlId="vehicles.actions.delete"
                            style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`delete`);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      )}

      <ConfirmDeleteDialog
        typeData="vehicles"
        open={dialog === `delete`}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={vehicles}
        handleDelete={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          deleteVehicles({ vehicleIds: vehicles.map((cli: any) => cli.id) });

          if (afterDeleteVehicles) {
            afterDeleteVehicles(vehicles);
          }
        }}
      />

      {activeProject && (
        <ConstraintsDialog
          typeData="vehicles"
          open={dialog === `constraints`}
          constraintsMap={[
            { id: `default_start_location`, type: `location` as ConstraintsType },
            { id: `default_end_location`, type: `location` as ConstraintsType },
            ...(activeProject?.view?.constraints.time_windows
              ? [{ id: `default_time_window`, type: `time_window` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.max_distance
              ? [{ id: `default_max_distance`, type: `kilometers` as ConstraintsType }]
              : []),
            ...((user?.isAdmin() || user?.isRoot()) && activeProject?.view?.constraints.min_distance
              ? [{ id: `default_min_distance`, type: `kilometers` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.max_time
              ? [{ id: `default_max_time`, type: `minutes` as ConstraintsType }]
              : []),
            ...((user?.isAdmin() || user?.isRoot()) && activeProject?.view?.constraints.min_time
              ? [{ id: `default_min_time`, type: `minutes` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.weight
              ? [{ id: `default_max_weight`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.volume
              ? [{ id: `default_max_volume`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.max_services
              ? [{ id: `default_max_services`, type: `number` as ConstraintsType }]
              : []),
            ...(activeProject?.view?.constraints.provide_requires
              ? [{ id: `default_provides`, type: `text_array` as ConstraintsType }]
              : []),
          ]}
          onCloseDialog={() => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
          idsToUpdate={vehicles.map((vehicle) => vehicle.id)}
          numberOfElements={vehicles.length}
          onUpdateConstraints={(updatedVehicles: Vehicle[]) => {
            setDialog(undefined);
            if (setContextAnchor) {
              setContextAnchor(undefined);
            }
          }}
        />
      )}

      <VehiclesCreatePlanDialog
        open={dialog === `create_plan`}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        selectedVehicles={vehicles}
        activeProject={activeProject}
      />
    </>
  );
};
